import React, { useState, useEffect } from 'react';
import { Volume2, VolumeX, Music, Zap, X } from 'lucide-react';
import { Button } from './ui/button';
import soundManager from '../utils/SoundManager';

const SoundSettings = ({ isOpen, onClose }) => {
  const [masterVol, setMasterVol] = useState(50);
  const [musicVol, setMusicVol] = useState(25);
  const [sfxVol, setSfxVol] = useState(60);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    soundManager.init();
  }, []);

  const handleMaster = (val) => {
    setMasterVol(val);
    soundManager.setMasterVolume(val / 100);
    setMuted(val === 0);
  };

  const handleMusic = (val) => {
    setMusicVol(val);
    soundManager.setMusicVolume(val / 100);
  };

  const handleSfx = (val) => {
    setSfxVol(val);
    soundManager.setSfxVolume(val / 100);
  };

  const toggleMute = () => {
    if (muted) {
      handleMaster(50);
    } else {
      handleMaster(0);
    }
  };

  const testSfx = () => {
    soundManager.init();
    soundManager.resume();
    soundManager.playPowerUp();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="sound-settings-overlay">
      <div className="glass-card p-6 w-full max-w-sm mx-4 border border-[#333]" data-testid="sound-settings-panel">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-game text-lg text-[#14F195] flex items-center gap-2">
            <Volume2 className="w-5 h-5" /> SOUND
          </h2>
          <Button onClick={onClose} variant="ghost" className="text-[#A1A1AA] hover:text-white p-1" data-testid="close-sound-settings">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="space-y-5">
          {/* Master Volume */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-[#A1A1AA] flex items-center gap-2">
                <button onClick={toggleMute} className="hover:text-white transition-colors" data-testid="mute-toggle">
                  {muted ? <VolumeX className="w-4 h-4 text-[#FF3B30]" /> : <Volume2 className="w-4 h-4" />}
                </button>
                Master
              </label>
              <span className="font-game text-xs text-white">{masterVol}%</span>
            </div>
            <input
              type="range" min="0" max="100" value={masterVol}
              onChange={e => handleMaster(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer"
              style={{ background: `linear-gradient(to right, #14F195 ${masterVol}%, #333 ${masterVol}%)` }}
              data-testid="master-volume-slider"
            />
          </div>

          {/* Music Volume */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-[#A1A1AA] flex items-center gap-2">
                <Music className="w-4 h-4" /> Music
              </label>
              <span className="font-game text-xs text-white">{musicVol}%</span>
            </div>
            <input
              type="range" min="0" max="100" value={musicVol}
              onChange={e => handleMusic(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer"
              style={{ background: `linear-gradient(to right, #9945FF ${musicVol}%, #333 ${musicVol}%)` }}
              data-testid="music-volume-slider"
            />
          </div>

          {/* SFX Volume */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm text-[#A1A1AA] flex items-center gap-2">
                <Zap className="w-4 h-4" /> Effects
              </label>
              <span className="font-game text-xs text-white">{sfxVol}%</span>
            </div>
            <input
              type="range" min="0" max="100" value={sfxVol}
              onChange={e => handleSfx(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer"
              style={{ background: `linear-gradient(to right, #FFD700 ${sfxVol}%, #333 ${sfxVol}%)` }}
              data-testid="sfx-volume-slider"
            />
          </div>

          {/* Test Button */}
          <Button onClick={testSfx} className="w-full bg-[#333] hover:bg-[#444] text-sm" data-testid="test-sound-btn">
            <Zap className="w-4 h-4 mr-2" /> Test Sound
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SoundSettings;
